import { useCallback, useMemo, useState, type ChangeEventHandler } from 'react'
// TODO: react-text-mask is deprecated — migrate to CurrencyField (src/core/currency/ui/CurrencyField.tsx)
import MaskedInput from 'react-text-mask'
import { toast } from 'react-toastify'
import styled from 'styled-components/macro'

import {
  Layout,
  Dialog,
  DatePicker,
  Label,
  TextField,
  Select,
  Textarea,
} from '@loadsmart/loadsmart-ui'
import { type Selectable } from '@loadsmart/loadsmart-ui/dist/hooks/useSelectable'
import type EventLike from '@loadsmart/loadsmart-ui/dist/utils/types/EventLike'
import { Text, Button, Field } from '@loadsmart/miranda-react'

import type { Option } from 'core/domain/Option'

import user from 'legacy/utils/user'

import { useConfigContext } from 'config/ui/ConfigContext'

import {
  LATE_CHECK_IN_REASON_OPTIONS,
  REASON_NOTE_MAX_LENGTH,
  type LateCheckInReasonCode,
  isLateCheckInReasonCode,
} from '../domain/LateCheckInReason'
import {
  isLateCheckIn,
  isLateReasonValid,
  isValidDeclaredDateTime,
} from '../presenters/lateCheckInReasonPresenter'
import { getPreviousDateTime } from '../presenters/stopPresenter'
import { useShipment } from './hooks/useShipment'
import { useShipmentAnalytics } from './hooks/useShipmentAnalytics'

const StyledDialogBody = styled(Dialog.Body)`
  margin-bottom: 0;
`

const StyledGrid = styled(Layout.Grid)`
  grid-template-columns: 50% 50%;
`

type ShipmentStopCheckDialogProps = {
  shipmentUUID: string
  stopId: string | number
  previousDateTime: string
  isOpen: boolean
  hideDialog: () => void
  callback: () => void
  stopType: string
  isLoading: boolean
  setIsLoading: (isLoading: boolean) => void
  appointmentWindowEnd?: string | null
  timezone?: string | null
  eventId?: number
}

export default function StopCheckDialog({
  shipmentUUID,
  stopId,
  previousDateTime,
  isOpen,
  hideDialog,
  callback,
  stopType,
  isLoading,
  setIsLoading,
  appointmentWindowEnd,
  timezone,
  eventId,
}: ShipmentStopCheckDialogProps) {
  const { submitShipmentStatus, removeShipmentStatus } = useShipment()
  const { track } = useShipmentAnalytics()
  const enableCheckInOutEdit = useConfigContext().enableCheckInOutEdit.toBoolean()
  const isCarrierUser = user.get('type') === 'carrier'

  const previousDate = getPreviousDateTime(previousDateTime, "yyyy-MM-dd'T'HH:mm:ss", timezone)
  const previousTime = getPreviousDateTime(previousDateTime, 'HH:mm', timezone)

  const [date, setDate] = useState<string | null | undefined>(previousDate)
  const [time, setTime] = useState<string | null | undefined>(previousTime)
  const [reasonCode, setReasonCode] = useState<LateCheckInReasonCode | ''>('')
  const [reasonNote, setReasonNote] = useState('')
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false)

  const canDelete = enableCheckInOutEdit && eventId != null && Boolean(previousDateTime)

  const isLate = useMemo(
    () =>
      enableCheckInOutEdit &&
      isLateCheckIn({
        stopType,
        date,
        time,
        appointmentWindowEnd,
        timezone,
      }),
    [appointmentWindowEnd, date, enableCheckInOutEdit, stopType, time, timezone]
  )

  const canSubmit =
    isValidDeclaredDateTime(date, time) &&
    !isLoading &&
    isLateReasonValid({
      isLate,
      reasonCode,
      reasonNote,
      reasonRequired: isCarrierUser,
    })

  const selectedReason = useMemo(
    () =>
      (LATE_CHECK_IN_REASON_OPTIONS.find(option => option.value === reasonCode) as
        | Option<LateCheckInReasonCode>
        | undefined) ?? null,
    [reasonCode]
  )

  const handleUpdate = useCallback(async () => {
    if (!date || !time || !isValidDeclaredDateTime(date, time)) return
    if (
      !isLateReasonValid({
        isLate,
        reasonCode,
        reasonNote,
        reasonRequired: isCarrierUser,
      })
    ) {
      return
    }

    setIsLoading(true)

    try {
      await submitShipmentStatus({
        shipmentUUID,
        stopId,
        type: `check-${stopType}`,
        date,
        time,
        ...(timezone ? { timezone } : {}),
        ...(isLate && reasonCode
          ? {
              reasonCode,
              ...(reasonNote.trim() ? { reasonNote: reasonNote.trim() } : {}),
            }
          : {}),
      })

      toast.success('Shipment updated successfully')
      track('Shipment Details / Agnostic / Milestone edition', {
        state: `check-${stopType}`,
      })

      callback()
      hideDialog()
    } catch {
      setIsLoading(false)
      toast.error('Could not update shipment, try again later')
    }
  }, [
    callback,
    date,
    hideDialog,
    isLate,
    reasonCode,
    reasonNote,
    setIsLoading,
    shipmentUUID,
    stopId,
    stopType,
    submitShipmentStatus,
    time,
    timezone,
    track,
    isCarrierUser,
  ])

  const handleClose = useCallback(() => {
    setIsConfirmingDelete(false)
    hideDialog()
  }, [hideDialog])

  const handleDelete = useCallback(async () => {
    if (eventId == null) return

    setIsLoading(true)

    try {
      await removeShipmentStatus({ shipmentUUID, stopId, eventId })

      toast.success('Event deleted successfully')

      callback()
      setDate(getPreviousDateTime('', "yyyy-MM-dd'T'HH:mm:ss", timezone))
      setTime(getPreviousDateTime('', 'HH:mm', timezone))
      setReasonCode('')
      setReasonNote('')
      handleClose()
    } catch {
      setIsLoading(false)
      toast.error('Could not delete event, try again later')
    }
  }, [
    callback,
    eventId,
    handleClose,
    removeShipmentStatus,
    setIsLoading,
    shipmentUUID,
    stopId,
    timezone,
  ])

  const handleDateChange = useCallback((event: EventLike<string | null>) => {
    setDate(event.target.value)
  }, [])

  const handleTimeChange = useCallback((event: EventLike<string | null>) => {
    setTime(event.target.value)
  }, [])

  const handleReasonChange = useCallback((event: EventLike<Selectable | Selectable[] | null>) => {
    const selected = event.target.value as Option<LateCheckInReasonCode> | null
    const nextCode = typeof selected?.value === 'string' ? selected.value : ''

    setReasonCode(isLateCheckInReasonCode(nextCode) ? nextCode : '')
    if (nextCode !== 'OTHER') {
      setReasonNote('')
    }
  }, [])

  const handleReasonNoteChange: ChangeEventHandler<HTMLTextAreaElement> = useCallback(event => {
    setReasonNote(event.target.value ?? '')
  }, [])

  if (isConfirmingDelete) {
    return (
      <Dialog open={isOpen} onOverlayClick={handleClose} scale="small">
        <Dialog.Header>Delete check-{stopType} event</Dialog.Header>
        <StyledDialogBody>
          <Layout.Stack space="l">
            <Text variant="body-md" color="color-text-primary">
              Are you sure you want to delete this check-{stopType} event? Once your action is
              confirmed, it cannot be undone.
            </Text>
            <Layout.Group space="s" justify="flex-end">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setIsConfirmingDelete(false)}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="danger"
                onClick={handleDelete}
                disabled={isLoading}
                loading={isLoading}
              >
                Yes, Delete Event
              </Button>
            </Layout.Group>
          </Layout.Stack>
        </StyledDialogBody>
      </Dialog>
    )
  }

  return (
    <Dialog open={isOpen} onOverlayClick={handleClose} scale="small">
      <Dialog.Header>Confirm check-{stopType}</Dialog.Header>
      <StyledDialogBody>
        <Layout.Stack space="l">
          <StyledGrid>
            <Layout.Stack space="xs">
              <Label htmlFor="date-picker">
                <Text color="color-text-tertiary">Date</Text>
              </Label>
              <DatePicker
                id="date-picker"
                name="date-picker"
                onChange={handleDateChange}
                value={date}
              />
            </Layout.Stack>
            <Layout.Stack space="xs">
              <Label htmlFor="time">
                <Text color="color-text-tertiary">Time (24-hour)</Text>
              </Label>
              <MaskedInput
                id="time"
                name="time"
                mask={[/[0-2]/, /\d/, ':', /[0-5]/, /\d/]}
                placeholder="00:00"
                aria-required="true"
                onChange={handleTimeChange}
                value={time || undefined}
                render={(ref: (inputElement: HTMLInputElement) => void, props) => (
                  <TextField ref={ref} {...props} />
                )}
              />
            </Layout.Stack>
          </StyledGrid>
          {isLate ? (
            <Layout.Stack space="s">
              <Field required={isCarrierUser}>
                <Field.Label id="late-reason-label">Reason for the delay</Field.Label>
                <Select
                  name="late-reason"
                  aria-label="Reason for the delay"
                  aria-labelledby="late-reason-label"
                  placeholder="Select a reason"
                  options={[...LATE_CHECK_IN_REASON_OPTIONS]}
                  value={selectedReason as Selectable}
                  onChange={handleReasonChange}
                  hideClear
                  disabled={isLoading}
                />
              </Field>
              <Field required={reasonCode === 'OTHER'}>
                <Field.Label id="late-reason-note-label">More details</Field.Label>
                <Textarea
                  aria-label="More details"
                  aria-labelledby="late-reason-note-label"
                  placeholder="Please provide more details about the reason for the delay"
                  value={reasonNote}
                  onChange={handleReasonNoteChange}
                  maxLength={REASON_NOTE_MAX_LENGTH}
                  disabled={isLoading}
                />
                <Field.Hint>
                  {reasonNote.length}/{REASON_NOTE_MAX_LENGTH}
                </Field.Hint>
              </Field>
            </Layout.Stack>
          ) : null}
          <Text variant="body-md" color="color-text-primary">
            {timezone
              ? `ATTENTION: Set date and time in the facility timezone (${timezone}).`
              : 'ATTENTION: Set date and time to match the facility timezone.'}
          </Text>
          <Button
            type="button"
            variant="primary"
            style={{ display: 'grid' }}
            onClick={handleUpdate}
            disabled={!canSubmit}
            loading={isLoading}
          >
            Confirm driver's {stopType === 'in' ? 'arrival ' : 'departure'}
          </Button>
          {canDelete ? (
            <Button
              type="button"
              variant="tertiary"
              style={{ display: 'grid' }}
              onClick={() => setIsConfirmingDelete(true)}
              disabled={isLoading}
            >
              Delete Event
            </Button>
          ) : null}
        </Layout.Stack>
      </StyledDialogBody>
    </Dialog>
  )
}
